import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { CalendarCheck, DollarSign, Users, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import adminService from '../services/adminService';
import { formatCurrency } from '../utils/formatters';

const PIE_COLORS = ['#6366f1', '#f59e0b', '#ef4444', '#10b981', '#3b82f6'];

export default function Analytics() {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [kpis, setKpis] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [bookingsByMonth, setBookingsByMonth] = useState([]);
  const [usersByRole, setUsersByRole] = useState([]);
  const [topVenues, setTopVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async (start, end) => {
    setLoading(true);
    try {
      const data = await adminService.getAnalytics(start, end);
      setKpis(data.kpis);
      setRevenueTrend(data.revenueTrend || []);
      setBookingsByMonth(data.bookingsByMonth || []);
      setUsersByRole(data.usersByRole || []);
      setTopVenues(data.topVenues || []);
    } catch (err) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDateRange = () => {
    loadAnalytics(dateRange.start || undefined, dateRange.end || undefined);
  };

  const kpiCards = kpis
    ? [
        { label: 'Total Bookings', value: kpis.totalBookings ?? 0, icon: CalendarCheck, color: 'bg-blue-500' },
        { label: 'Total Revenue', value: formatCurrency(kpis.totalRevenue ?? 0), icon: DollarSign, color: 'bg-green-500' },
        { label: 'Avg Booking Value', value: formatCurrency(kpis.avgBookingValue ?? 0), icon: TrendingUp, color: 'bg-admin-500' },
        { label: 'Active Players', value: kpis.activeUsers ?? 0, icon: Users, color: 'bg-amber-500' },
      ]
    : [];

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="w-8 h-8 border-4 border-admin-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1F2937]">Analytics</h1>
          <p className="text-sm text-[#667085] mt-1">Platform performance and insights</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange((p) => ({ ...p, start: e.target.value }))}
            className="admin-input text-sm py-1.5"
          />
          <span className="text-[#667085]">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange((p) => ({ ...p, end: e.target.value }))}
            className="admin-input text-sm py-1.5"
          />
          <button onClick={handleApplyDateRange} className="admin-btn-primary text-sm py-1.5">
            Apply
          </button>
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {kpiCards.map((kpi) => (
            <div key={kpi.label} className="admin-card flex items-center gap-4">
              <div className={`w-12 h-12 ${kpi.color} rounded-xl flex items-center justify-center`}>
                <kpi.icon className="text-[#1F2937]" size={22} />
              </div>
              <div>
                <p className="text-sm text-[#667085]">{kpi.label}</p>
                <p className="text-2xl font-bold text-[#1F2937]">{kpi.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="admin-card">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4">Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" tickFormatter={(v) => `$${v}`} />
              <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
              <Line type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={2} dot={{ fill: '#6366f1', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-card">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4">Bookings by Month</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bookingsByMonth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <Tooltip />
              <Bar dataKey="bookings" fill="#818cf8" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-card">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4">Users by Role</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={usersByRole}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="count"
                nameKey="role"
                label={({ role, count }) => `${role}: ${count}`}
              >
                {usersByRole.map((_, idx) => (
                  <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-card">
          <h3 className="text-sm font-semibold text-[#1F2937] mb-4">Top Venues by Bookings</h3>
          {topVenues.length === 0 ? (
            <p className="text-center py-8 text-[#667085] text-sm">No venue data available</p>
          ) : (
            <div className="space-y-3">
              {topVenues.map((venue, idx) => (
                <div key={venue.id || idx} className="flex items-center gap-3 p-3 bg-[#F9FAFB] rounded-lg">
                  <span className="w-6 h-6 bg-admin-100 rounded-full flex items-center justify-center text-xs font-bold text-admin-700">
                    {idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#1F2937] truncate">{venue.name}</p>
                    <p className="text-xs text-[#667085]">{venue.city}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-[#1F2937]">{venue.bookings}</p>
                    <p className="text-xs text-[#667085]">bookings</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
